import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
	AlertFillCircleIcon,
	CheckCircleFillIcon,
	MailFilledIcon
} from '@/components/ui/icons'
import { ResponsiveConfirmDialog } from '@/features/marquee/components/responsive-confirm-dialog'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { handleOperatorError, teamFirstName } from '@/features/marquee/utils'
import {
	deactivateOperator,
	type MarqueeManagedOperator,
	reactivateOperator,
	resendOperatorInvitation
} from '@/sdk'
import { Trans, useTranslation } from '@/translations/i18n'

export type TeamMemberConfirmAction =
	| 'deactivate'
	| 'reactivate'
	| 'resend_invite'

interface TeamMemberConfirmDialogProps {
	action: TeamMemberConfirmAction
	member: MarqueeManagedOperator
	onClose: () => void
}

/**
 * The three Team writes that are confirmed before they fire: deactivate,
 * reactivate and resend invite. One dialog because they share everything but
 * the copy and the call.
 *
 * Controlled by the page and mounted only while open: the row menu and the
 * edit dialog both hand the action back to the page rather than mounting a
 * dialog of their own, so the deactivate reached from the edit form is the
 * same one the menu opens.
 *
 * Every write refetches the roster and the market-manager picker
 * (`listMarketManagers`), so event forms see the change: a deactivated market
 * manager stops being assignable, and a resend flips nothing there but costs
 * nothing to refresh either.
 *
 * The staging-only activation link in the resend response is not shown.
 */
function TeamMemberConfirmDialog({
	action,
	member,
	onClose
}: TeamMemberConfirmDialogProps) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const name = member.name ?? member.email
	const firstName = teamFirstName(member)

	const confirm = useMutation({
		mutationFn: async () => {
			const path = { operatorId: member.id }
			if (action === 'resend_invite') {
				await resendOperatorInvitation({ path })
				return
			}
			await (action === 'deactivate'
				? deactivateOperator({ path })
				: reactivateOperator({ path }))
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.marquee.roster.all()
				}),
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.marquee.marketManagers.all()
				})
			])
			toast.success(
				{
					deactivate: t('marquee:team_deactivated_toast', { name }),
					reactivate: t('marquee:team_reactivated_toast', { name }),
					resend_invite: t('marquee:team_invite_resent_toast', {
						email: member.email
					})
				}[action]
			)
			onClose()
		},
		onError: (error) => handleOperatorError(error)
	})

	const shared = {
		isPending: confirm.isPending,
		onConfirm: () => confirm.mutate(),
		onOpenChange: (open: boolean) => {
			if (!open) {
				onClose()
			}
		},
		open: true,
		showCloseButton: true,
		children: null
	}

	if (action === 'deactivate') {
		return (
			<ResponsiveConfirmDialog
				{...shared}
				body={<DeactivateConsequences member={member} />}
				confirmLabel={t('marquee:team_action_deactivate')}
				description={t('marquee:team_deactivate_dialog_description', {
					firstName
				})}
				icon={
					<AlertFillCircleIcon
						className='mx-auto text-red-700'
						size={32}
					/>
				}
				title={t('marquee:team_deactivate_dialog_title', { name })}
			/>
		)
	}

	if (action === 'reactivate') {
		return (
			<ResponsiveConfirmDialog
				{...shared}
				confirmLabel={t('marquee:team_action_reactivate')}
				description={t('marquee:team_reactivate_dialog_description', {
					firstName
				})}
				icon={
					<CheckCircleFillIcon
						className='mx-auto text-green-700'
						size={32}
					/>
				}
				title={t('marquee:team_reactivate_dialog_title', { name })}
			/>
		)
	}

	return (
		<ResponsiveConfirmDialog
			{...shared}
			confirmLabel={t('marquee:team_resend_invite_confirm')}
			description={
				<Trans
					components={{
						strong: <strong className='font-semibold' />
					}}
					i18nKey='marquee:team_resend_invite_dialog_description'
					values={{ email: member.email }}
				/>
			}
			icon={
				<MailFilledIcon className='mx-auto text-grey-1000' size={30} />
			}
			title={t('marquee:team_resend_invite_dialog_title', { firstName })}
		/>
	)
}

/**
 * What the deactivation does and does not touch, worded as the design has it:
 * access ends on their next request, nothing is removed from past events or
 * exports, and they can be reactivated. Never worded as a delete.
 *
 * The assigned-events line is the one an admin would not guess: nothing is
 * reassigned for them, so it says how many upcoming events still list the
 * person (`upcoming_assigned_events`), and is left out when there are none.
 * Neutral pronouns where the design has "her": there is no gender data.
 */
function DeactivateConsequences({
	member
}: {
	member: MarqueeManagedOperator
}) {
	const { t } = useTranslation()
	const firstName = teamFirstName(member)

	return (
		<div className='mt-6 flex flex-col gap-2 rounded-2xl bg-grey-100 p-4 text-sm/4.5'>
			<p className='font-medium text-grey-1000 text-md/5'>
				{t('marquee:team_deactivate_what_happens')}
			</p>
			<ul className='ml-5 list-disc text-neutral-500'>
				<li>
					{t('marquee:team_deactivate_loses_access', { firstName })}
				</li>
				<li>{t('marquee:team_deactivate_history_kept')}</li>
				{member.upcoming_assigned_events > 0 && (
					<li>
						{t('marquee:team_deactivate_assigned_events', {
							name: member.name ?? member.email,
							count: member.upcoming_assigned_events
						})}
					</li>
				)}
				<li>{t('marquee:team_deactivate_can_reactivate')}</li>
			</ul>
		</div>
	)
}

export { TeamMemberConfirmDialog }
